/**
 * Public marketing homepage — Phase 4.5 (Light Mode + Indigo/Emerald).
 * Aligns with /app/design_guidelines.json and the new in-app top-bar style.
 * All previous testids preserved.
 */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import {
    ArrowUpRight, ArrowRight, ShieldCheck, Sparkles, Workflow, Briefcase, Gavel, FileText,
    Users, BarChart3, Check, Clock, Receipt, Scale
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1764410481612-7544525b2991?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBsYXclMjBmaXJtJTIwb2ZmaWNlfGVufDB8fHx8MTc4MDc1OTk0OHww&ixlib=rb-4.1.0&q=85";
const PORTRAIT_1 = "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBsYXd5ZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODA3NTk5NDh8MA&ixlib=rb-4.1.0&q=85";
const PORTRAIT_2 = "https://images.unsplash.com/photo-1662104935883-e9dd0619eaba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBsYXd5ZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODA3NTk5NDh8MA&ixlib=rb-4.1.0&q=85";

const MODULES = [
    { icon: Users,       name: "Client Management", desc: "Codes, KYC, timelines, duplicate detection — every client tracked." },
    { icon: Briefcase,   name: "Litigation Lifecycle",    desc: "From filing to disposal. Multi-court, multi-stage, multi-advocate." },
    { icon: Gavel,       name: "Court Diary",       desc: "Hearings calendar, outcomes, automated next-date generation." },
    { icon: FileText,    name: "Documents",         desc: "Petitions, agreements, evidence. Versioned. AI-summarised on demand." },
    { icon: Sparkles,    name: "Legal AI",          desc: "Claude-powered research assistant. Drafts, summaries, citations." },
    { icon: Workflow,    name: "Automations",       desc: "WhatsApp on hearing day. Reminders. Escalations. Drag & drop." },
    { icon: ShieldCheck, name: "Compliance",        desc: "RBAC, audit logs, tenant isolation, enterprise-grade encryption." },
    { icon: BarChart3,   name: "Analytics",         desc: "Revenue, productivity, outstanding fees. Reports for partners." },
];

const STATS = [
    { value: "40%",   label: "Drop in outstanding fees" },
    { value: "1-3",   label: "Clicks per workflow" },
    { value: "256-bit", label: "Tenant-isolated encryption" },
    { value: "14 day", label: "Free trial · no card" },
];

function PricingCycle({ plan }) {
    const map = {
        quarterly: "/quarter",
        half_yearly: "/6 months",
        annual: "/year",
        bi_annual: "/2 years",
        tri_annual: "/3 years",
    };
    return <span className="text-sm text-muted-foreground ml-1">{map[plan.billing_cycle] || ""}</span>;
}

export default function Landing() {
    const [plans, setPlans] = useState([]);
    const branding = useBranding();
    const logoH = Math.max(24, Math.min(56, Number(branding.logo_display_height) || 32));
    const layout = branding.logo_layout || "icon_and_text";
    const showIcon = layout !== "text_only";
    const showText = layout !== "icon_only";
    const [appName1, ...appNameRest] = (branding.app_name || "Law OS").split(" ");
    const appName2 = appNameRest.join(" ");
    const [voices, setVoices] = useState(null);
    useEffect(() => {
        api.get("/plans").then(r => {
            // Sort cheapest-first; Enterprise (₹0 / custom-pricing) always last.
            const sorted = [...(r.data || [])].sort((a, b) => {
                const aEnt = a.name === "Enterprise";
                const bEnt = b.name === "Enterprise";
                if (aEnt && !bEnt) return 1;
                if (bEnt && !aEnt) return -1;
                return (a.price || 0) - (b.price || 0);
            });
            setPlans(sorted);
        }).catch(() => {});
        api.get("/public/voices").then(r => setVoices(r.data)).catch(() => {});
    }, []);

    const isCustom = (p) => p.name === "Enterprise";
    const isFree   = (p) => !p.price && p.name !== "Enterprise";
    // Legacy /api/public/plans endpoint doesn't expose `code` so we map by name
    // to keep deep-linking from the Landing pricing cards working with the
    // new pay-now register flow (`/register?plan=<code>`).
    const PLAN_CODE_BY_NAME = {
        "Starter":          "starter",
        "Founding Member":  "founder",
        "Single Advocate":  "single",
        "Chambers":         "chambers",
        "Firm Starter":     "firm_starter",
        "Firm Pro":         "firm_pro",
    };
    const planRegisterUrl = (p) => {
        if (isCustom(p)) return "/legal/contact";
        const code = PLAN_CODE_BY_NAME[p.name];
        return code ? `/register?plan=${code}` : "/register";
    };

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="public-landing">
            {/* ──── Top bar ──── */}
            <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-card/90 border-b border-border">
                <div className="max-w-7xl mx-auto h-16 flex items-center justify-between px-4 sm:px-6 lg:px-12">
                    <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
                        {showIcon && (branding.logo_src ? (
                            <img src={branding.logo_src} alt={branding.app_name}
                                style={{ height: logoH, width: "auto", maxWidth: logoH * 2.5 }}
                                className="object-contain"
                                onError={(e) => { e.target.style.display = "none"; }}/>
                        ) : (
                            <div style={{ height: logoH, width: logoH }}
                                className="rounded-lg bg-primary flex items-center justify-center shrink-0">
                                <span className="font-heading text-primary-foreground font-bold"
                                    style={{ fontSize: logoH * 0.45 }}>{(branding.app_name || "L")[0]}</span>
                            </div>
                        ))}
                        {showText && (
                            <div>
                                <div className="font-heading text-[15px] leading-none font-semibold tracking-tight">
                                    {appName1}{appName2 && <span className="text-primary"> {appName2}</span>}
                                </div>
                                {branding.app_tagline && (
                                    <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground mt-0.5">
                                        Legal · Operating · System
                                    </div>
                                )}
                            </div>
                        )}
                    </Link>
                    <nav className="hidden md:flex items-center gap-1">
                        {[
                            ["modules", "Modules"],
                            ["pricing", "Pricing"],
                            ["voices",  "Voices"],
                        ].map(([id, label]) => (
                            <a key={id} href={`#${id}`}
                                className="text-sm text-foreground/75 hover:text-primary px-3 py-2 rounded-lg hover:bg-muted transition-colors">
                                {label}
                            </a>
                        ))}
                        <Link to="/pricing" className="text-sm text-foreground/75 hover:text-primary px-3 py-2 rounded-lg hover:bg-muted transition-colors">Plans</Link>
                        <Link to="/legal/contact" className="text-sm text-foreground/75 hover:text-primary px-3 py-2 rounded-lg hover:bg-muted transition-colors">Contact</Link>
                        <Link to="/partner/login" data-testid="landing-partner-link"
                            className="text-sm text-foreground/75 hover:text-primary px-3 py-2 rounded-lg hover:bg-muted transition-colors border border-border hover:border-primary/40">
                            Partner Portal
                        </Link>
                    </nav>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <Link to="/login" data-testid="landing-signin"
                            className="text-sm text-foreground/75 hover:text-primary px-2 sm:px-4 py-2 rounded-lg hover:bg-muted transition-colors">
                            Sign in
                        </Link>
                        <Link to="/register" data-testid="landing-cta-start"
                            className="text-xs sm:text-sm bg-primary text-primary-foreground hover:opacity-90 px-3 sm:px-5 py-2 rounded-lg font-medium transition-opacity shadow-lawos-sm whitespace-nowrap inline-flex items-center gap-1.5">
                            Start free trial <ArrowRight className="w-3.5 h-3.5"/>
                        </Link>
                    </div>
                </div>
            </header>

            {/* ──── Hero ──── */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                {/* Soft decorative gradients (subtle, light) */}
                <div aria-hidden className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-primary/8 blur-3xl"/>
                    <div className="absolute top-40 -right-32 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-3xl"/>
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <span data-testid="hero-overline"
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-medium uppercase tracking-[0.18em]">
                            <Sparkles className="w-3 h-3"/> Built for India&apos;s legal profession
                        </span>
                        <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground">
                            The operating system <br className="hidden sm:block"/>
                            for <span className="text-primary">advocates &amp; firms</span>.
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                            One subscription replaces seven tools — case management, court diary, billing,
                            documents, communications and AI — engineered with the rigour the practice demands.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/register" data-testid="hero-cta-primary"
                                className="group inline-flex items-center gap-2 bg-primary text-primary-foreground hover:opacity-90 px-6 py-3 rounded-xl font-medium transition-opacity shadow-lawos-md">
                                Begin 14-day trial
                                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2}/>
                            </Link>
                            <a href="#pricing" data-testid="hero-cta-secondary"
                                className="inline-flex items-center gap-2 border border-border hover:border-primary hover:text-primary px-6 py-3 rounded-xl transition-colors text-foreground/85">
                                Explore pricing
                            </a>
                        </div>
                        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600"/> Bar Council compliant</span>
                            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600"/> Multi-tenant secure</span>
                            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600"/> Razorpay AutoPay</span>
                        </div>
                    </div>

                    {/* Hero visual — light card mock of dashboard */}
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-primary/15 via-transparent to-emerald-500/15 blur-2xl"/>
                            <div className="relative bg-card border border-border rounded-2xl shadow-lawos-lg overflow-hidden">
                                <img src={HERO_IMG} alt="" className="w-full h-44 object-cover"/>
                                <div className="p-5 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">Litigation Workspace</div>
                                            <div className="font-heading text-base font-semibold mt-0.5">Acme Industries v. Doe Holdings</div>
                                        </div>
                                        <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">On track</span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        <MockStat icon={Gavel} label="Next hearing" value="12 Jul"/>
                                        <MockStat icon={Clock} label="Open tasks"   value="4"/>
                                        <MockStat icon={Receipt} label="Due ₹"      value="35k"/>
                                    </div>
                                    <div className="rounded-xl bg-primary/5 border border-primary/15 p-3">
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold mb-1 inline-flex items-center gap-1">
                                            <Sparkles className="w-3 h-3"/> AI summary
                                        </div>
                                        <p className="text-xs text-foreground/80 leading-relaxed">
                                            Commercial contract dispute · arbitration clause invoked. Tribunal constituted, statement of claim due in 21 days. Suggested next steps: collate correspondence, prepare claim outline.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ──── Stats strip ──── */}
            <section className="border-y border-border bg-card/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {STATS.map((s, i) => (
                        <div key={i} className="text-center lg:text-left" data-testid={`landing-stat-${i}`}>
                            <div className="font-heading text-3xl sm:text-4xl font-bold text-primary">{s.value}</div>
                            <div className="text-xs text-muted-foreground mt-1 uppercase tracking-[0.16em]">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ──── Modules bento ──── */}
            <section id="modules" className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <div className="grid lg:grid-cols-12 gap-12 mb-12">
                        <div className="lg:col-span-5">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-primary font-semibold mb-3">Modules</div>
                            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground">
                                Every workflow your firm runs — <span className="text-primary">under one ledger</span>.
                            </h2>
                        </div>
                        <p className="lg:col-span-6 lg:col-start-7 text-muted-foreground leading-relaxed text-base sm:text-lg pt-2">
                            From the first consultation to the final hearing, {branding.app_name || "Law OS"} keeps your firm aligned.
                            Nothing is hardcoded; every category, stage, fee type and template adapts to how your chambers
                            actually work.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {MODULES.map((m, i) => (
                            <div key={i} data-testid={`module-${i}`}
                                className="bg-card rounded-2xl border border-border p-6 hover:border-primary/40 hover:shadow-lawos-md transition-all group">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                                    <m.icon className="w-5 h-5 text-primary" strokeWidth={1.75}/>
                                </div>
                                <div className="font-heading text-lg font-semibold mb-2 text-foreground">{m.name}</div>
                                <div className="text-sm text-muted-foreground leading-relaxed">{m.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ──── Pricing ──── */}
            <section id="pricing" className="py-24 bg-muted/30 border-y border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <div className="text-center mb-14 max-w-2xl mx-auto">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-primary font-semibold mb-3">Pricing</div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground">
                            Pay quarterly. <span className="text-primary">Or commit for three years.</span>
                        </h2>
                        <p className="mt-4 text-muted-foreground">Every plan, every cycle. AutoPay via Razorpay. Cancel any time.</p>
                        <p className="mt-2 text-xs text-muted-foreground/80">
                            Subscription fees do not include AI tokens or third-party API usage (WhatsApp / SMS / Email send) — those are billed at cost.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {(() => {
                            // Pick exactly ONE "Most chosen" plan — the
                            // CMS-pinned plan (`is_marketing_featured`) that
                            // is neither the Founder one-time SKU nor
                            // Enterprise. Falls back to the second card if
                            // the admin hasn't pinned anything.
                            const eligible = (p) => p && !isCustom(p) && p.name !== "Founding Member";
                            const cmsPick = plans.find(x => x.is_marketing_featured && eligible(x));
                            const fallbackPick = plans.filter(eligible)[1];
                            const featuredCode = (cmsPick || fallbackPick)?.id;
                            return plans.map((p) => {
                                const featured = p.id === featuredCode;
                                return (
                                <div key={p.id} data-testid={`plan-${p.name.toLowerCase()}`}
                                    className={`relative bg-card rounded-2xl p-8 border transition-all ${
                                        featured
                                            ? "border-primary shadow-lawos-lg lg:scale-[1.03] lg:z-10"
                                            : "border-border hover:border-primary/40 hover:shadow-lawos-md"
                                    }`}>
                                    {featured && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background dark:bg-primary dark:text-primary-foreground text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-full font-medium shadow-lawos-sm">
                                            Most chosen
                                        </div>
                                    )}
                                    <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-semibold mb-3">{p.name}</div>
                                    <div className="flex items-baseline gap-1">
                                        {isCustom(p) ? (
                                            <span className="font-heading text-4xl font-bold text-foreground" data-testid={`plan-price-${p.name.toLowerCase()}`}>Custom</span>
                                        ) : isFree(p) ? (
                                            <>
                                                <span className="font-heading text-4xl font-bold text-foreground" data-testid={`plan-price-${p.name.toLowerCase()}`}>Free</span>
                                                <span className="text-xs text-muted-foreground ml-1">forever</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="font-heading text-4xl font-bold text-foreground" data-testid={`plan-price-${p.name.toLowerCase()}`}>₹{p.price.toLocaleString("en-IN")}</span>
                                                <PricingCycle plan={p}/>
                                            </>
                                        )}
                                    </div>
                                    <p className="mt-4 text-sm text-muted-foreground min-h-[3rem]">{p.description}</p>
                                    <ul className="space-y-2.5 text-sm mt-6 mb-8">
                                        {p.features.map((f, j) => (
                                            <li key={j} className="flex items-start gap-2.5 text-foreground/85">
                                                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" strokeWidth={2}/>
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Link to={planRegisterUrl(p)} data-testid={`plan-cta-${p.name.toLowerCase()}`}
                                        className={`block text-center py-2.5 rounded-xl font-medium text-sm transition-all ${
                                            featured
                                                ? "bg-primary text-primary-foreground hover:opacity-90 shadow-lawos-sm"
                                                : "bg-card border border-border hover:border-primary hover:text-primary text-foreground"
                                        }`}>
                                        {isCustom(p) ? "Talk to Sales" : isFree(p) ? "Start Free" : `Choose ${p.name}`}
                                    </Link>
                                </div>
                                );
                            });
                        })()}
                    </div>
                </div>
            </section>

            {/* ──── Voices ──── */}
            <section id="voices" className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <div className="mb-12 max-w-3xl">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-primary font-semibold mb-3">
                            {voices?.section_label || "Voices"}
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground">
                            {(voices?.heading_segments?.length
                                ? voices.heading_segments
                                : [
                                    {text:"Trusted from the ", color:"default"},
                                    {text:"bar bench", color:"primary"},
                                    {text:" to the ", color:"default"},
                                    {text:"boardroom", color:"emerald"},
                                    {text:".", color:"default"},
                                ]).map((seg, i) => {
                                    const cls = seg.color === "primary" ? "text-primary"
                                        : seg.color === "emerald" ? "text-emerald-600"
                                        : seg.color === "rose"    ? "text-rose-600"
                                        : seg.color === "amber"   ? "text-amber-600"
                                        : seg.color === "sky"     ? "text-sky-600"
                                        : "";
                                    return cls
                                        ? <span key={i} className={cls}>{seg.text}</span>
                                        : <span key={i}>{seg.text}</span>;
                                })}
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        {(voices?.cards?.length
                            ? voices.cards
                            : [
                                { id:"d1", portrait:PORTRAIT_1, quote:"My junior advocates know exactly what's coming up tomorrow without asking. That's the win.", name:"Adv. R. Krishnan", role:"Senior Partner, Chennai" },
                                { id:"d2", portrait:PORTRAIT_2, quote:"Outstanding fees dropped 40% in two quarters. The AutoPay nudges do the chasing for us.", name:"Adv. Mehra & Co.", role:"Managing Partner, Delhi" },
                            ]).map((c) => (
                            <TestimonialCard key={c.id}
                                portrait={c.portrait}
                                quote={c.quote}
                                name={c.name}
                                role={c.role}/>
                        ))}
                    </div>
                </div>
            </section>

            {/* ──── Become a Partner Banner ──── */}
            <section className="py-16 border-y border-border bg-card/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-8">
                        <div className="flex items-start gap-5">
                            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                                <Users className="w-6 h-6 text-primary" strokeWidth={1.75}/>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.22em] text-primary font-semibold mb-1">Channel Partners</div>
                                <h3 className="font-heading text-xl sm:text-2xl font-bold text-foreground leading-snug">
                                    Earn by referring law firms &amp; advocates.
                                </h3>
                                <p className="mt-2 text-sm text-muted-foreground max-w-lg">
                                    Join our 5-tier partner network. Refer firms, earn commissions on every subscription, and get paid automatically via Razorpay AutoPay.
                                </p>
                            </div>
                        </div>
                        <Link to="/partner/login" data-testid="become-partner-cta"
                            className="shrink-0 inline-flex items-center gap-2 border border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3 rounded-xl font-medium text-sm transition-all whitespace-nowrap">
                            Become a Partner <ArrowUpRight className="w-4 h-4" strokeWidth={2}/>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ──── Final CTA ──── */}
            <section className="pb-24">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12">
                    <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground p-12 sm:p-16 text-center shadow-lawos-lg">
                        <div aria-hidden className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-3xl"/>
                        <div aria-hidden className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-emerald-300/20 blur-3xl"/>
                        <h2 className="relative font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                            Sign your chambers into the <span className="text-emerald-300">next decade</span>.
                        </h2>
                        <p className="relative mt-5 text-primary-foreground/85 max-w-xl mx-auto">
                            Fourteen days. Every module unlocked. No credit card required.
                        </p>
                        <Link to="/register" data-testid="footer-cta"
                            className="relative inline-flex items-center gap-2 mt-8 bg-white text-primary hover:bg-emerald-50 px-7 py-3 rounded-xl font-semibold transition-colors shadow-lawos-md">
                            Begin trial <ArrowUpRight className="w-4 h-4" strokeWidth={2}/>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ──── Footer ──── */}
            <footer className="border-t border-border bg-card/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-3">
                        <Scale className="w-4 h-4 text-primary" strokeWidth={1.75}/>
                        <span>© {new Date().getFullYear()} {branding.app_owner || "Inno Aim Private Limited"}. All rights reserved.</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        <Link to="/pricing" className="hover:text-primary">Pricing</Link>
                        <Link to="/partner/login" className="hover:text-primary" data-testid="footer-partner-link">Partners</Link>
                        <Link to="/legal/terms" className="hover:text-primary">Terms</Link>
                        <Link to="/legal/privacy" className="hover:text-primary">Privacy</Link>
                        <Link to="/legal/refunds" className="hover:text-primary">Refunds</Link>
                        <Link to="/legal/contact" className="hover:text-primary">Contact</Link>
                        <span className="font-mono">{branding.app_name || "Law OS"} v2.32</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function MockStat({ icon: Icon, label, value }) {
    return (
        <div className="bg-muted/40 rounded-lg p-2.5">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                <Icon className="w-2.5 h-2.5"/> {label}
            </div>
            <div className="text-sm font-semibold text-foreground mt-0.5">{value}</div>
        </div>
    );
}

function TestimonialCard({ portrait, quote, name, role }) {
    // Prepend backend URL when the API returns a relative `/api/voices/...` path
    const src = portrait && portrait.startsWith("/api/")
        ? `${process.env.REACT_APP_BACKEND_URL || ""}${portrait}`
        : portrait;
    return (
        <div className="bg-card rounded-2xl border border-border p-8 flex gap-5 hover:border-primary/40 hover:shadow-lawos-md transition-all">
            <img src={src} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0"/>
            <div>
                <p className="font-heading text-lg text-foreground leading-snug mb-4">&ldquo;{quote}&rdquo;</p>
                <div className="text-[11px] uppercase tracking-[0.22em] text-primary font-semibold" dangerouslySetInnerHTML={{ __html: name }}/>
                <div className="text-sm text-muted-foreground mt-1">{role}</div>
            </div>
        </div>
    );
}
